/**
 * Work Orders API layer.
 *
 * Every call is live — there is no mock data in this module.
 */

import { WORK_ORDER_ROUTES } from '../../../config/api-routes';
import { WORK_ORDER_API_STATUS, WORK_ORDER_STATUS } from '../../../utility/Constant';
import instance from '../../../config/instance';
import { paramsToQueryString } from '../../../utility/helpers/commonFunction';

/** Drop empty params so the query string never carries `status=&date=`. */
const cleanParams = (params = {}) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));

/** GET → { sequenceNumber, workOrderNumber } */
export const generateWorkOrderNumberApi = async () => {
    const response = await instance.get(WORK_ORDER_ROUTES.GENERATE_WORK_ORDER_NUMBER);
    return response?.data?.data;
};

/**
 * Work order listing.
 *
 * GET work-order-authorization/list
 * params: pagination, limit, page, status, date, vehicleId, sortBy, sortType, timezone
 * returns: { status, message, totalPages, totalCount, currentPage, data: [] }
 */
export const workOrderListApi = async (params) => {
    const queryParams = paramsToQueryString(cleanParams(params));
    const response = await instance.get(WORK_ORDER_ROUTES.WORK_ORDER_LIST + queryParams);

    return {
        data: response?.data?.data || [],
        totalCount: response?.data?.totalCount || 0,
        totalPages: response?.data?.totalPages || 0,
        currentPage: response?.data?.currentPage || 1,
    };
};

/** GET work-order-authorization/get/:id */
export const workOrderDetailApi = async (id) => {
    const response = await instance.get(`${WORK_ORDER_ROUTES.GET_WORK_ORDER}/${id}`);
    return response?.data?.data;
};

/**
 * GET work-order-authorization/history/:id?sortType=DSC
 * → [{ title, description, date, workOrderNumber, workOrderAuthorizationId }]
 */
export const workOrderHistoryApi = async (id, params = {}) => {
    const queryParams = paramsToQueryString(cleanParams(params));
    const response = await instance.get(WORK_ORDER_ROUTES.HISTORY(id) + queryParams);
    return response?.data?.data || [];
};

/** POST — `isDraft` decides whether the work order lands as a draft or as open. Accepts optional params (e.g. { sendEmail: true }). */
export const createWorkOrderApi = async (payload, params = {}) => {
    const queryParams = paramsToQueryString(cleanParams(params));
    const response = await instance.post(WORK_ORDER_ROUTES.CREATE_WORK_ORDER + queryParams, payload);
    return response?.data?.data;
};

/** PUT — the update payload carries no `isDraft`; the backend owns the status. Accepts optional params (e.g. { sendEmail: true }). */
export const updateWorkOrderApi = async (id, payload, params = {}) => {
    const queryParams = paramsToQueryString(cleanParams(params));
    const response = await instance.put(WORK_ORDER_ROUTES.UPDATE_WORK_ORDER(id) + queryParams, payload);
    return response?.data?.data;
};

export const deleteWorkOrderApi = async (id) => {
    const response = await instance.delete(WORK_ORDER_ROUTES.DELETE_WORK_ORDER(id));
    return response?.data;
};

/**
 * PUT work-order-authorization/change-status/:id  { status }
 *
 * One endpoint drives every status transition. `status` must be the API's own value —
 * pass it through WORK_ORDER_API_STATUS rather than sending the UI's spelling.
 */
export const changeWorkOrderStatusApi = async (id, status) => {
    const response = await instance.put(WORK_ORDER_ROUTES.CHANGE_STATUS(id), { status });
    return response?.data?.data ?? response?.data;
};

/** Mark as Authorized — the inventory consumption event (spec §9 W2/W3). */
export const markWorkOrderAuthorizedApi = async (id) => changeWorkOrderStatusApi(id, WORK_ORDER_API_STATUS[WORK_ORDER_STATUS.AUTHORIZED]);

export const cancelWorkOrderApi = async (id) => changeWorkOrderStatusApi(id, WORK_ORDER_API_STATUS[WORK_ORDER_STATUS.CANCELLED]);

/**
 * POST register-action/:type/:id
 *
 * Records an action so it shows up in the work order's history. The PDF itself is served
 * straight from `pdfUrl`, so the frontend tells the backend a download happened rather than
 * the backend inferring it. `type` is dynamic — see WOA_REGISTER_ACTION.
 */
export const registerWorkOrderActionApi = async (type, id) => {
    const response = await instance.post(WORK_ORDER_ROUTES.REGISTER_ACTION(type, id));
    return response?.data;
};

export const convertWorkOrderToInvoiceApi = async (id) => {
    const response = await instance.post(WORK_ORDER_ROUTES.CONVERT_TO_INVOICE(id));
    return response?.data?.data;
};

/**
 * POST work-order-authorization/send-work-order-pdf/:id
 * Sends the Work Order PDF via email to the customer.
 */
export const sendWorkOrderEmailApi = async (id) => {
    const response = await instance.post(WORK_ORDER_ROUTES.SEND_WORK_ORDER_EMAIL(id));
    return response?.data?.data ?? response?.data;
};

/**
 * GET — the open work order draft for a vehicle: `{ vehicleId }`.
 * → the draft record, or null when there is none.
 */
export const findOpenWorkOrderDraftApi = async (params) => {
    const response = await instance.get(WORK_ORDER_ROUTES.FIND_OPEN_DRAFT, { params });
    const data = response?.data?.data;
    return data?.exists ? data.draft || null : null;
};
